import { create } from 'zustand'
import { api } from '../api/client.js'

interface ApprovalRow {
  reviewed: boolean
  stateName: string | null
}

/** Count how a reviewed row's tenant-defined state name maps to approve/reject. */
function isApproved(r: ApprovalRow): boolean {
  return r.reviewed && (r.stateName?.toLowerCase().includes('approv') ?? false)
}
function isRejected(r: ApprovalRow): boolean {
  return r.reviewed && (r.stateName?.toLowerCase().includes('reject') ?? false)
}

export interface ApprovalCounts {
  pending: number
  approved: number
  rejected: number
  requested: number
  requestedApproved: number
  requestedRejected: number
  requestedPending: number
}

const EMPTY: ApprovalCounts = {
  pending: 0,
  approved: 0,
  rejected: 0,
  requested: 0,
  requestedApproved: 0,
  requestedRejected: 0,
  requestedPending: 0,
}

interface ApprovalCountsState {
  counts: ApprovalCounts
  /** Full replace — called after ApprovalDropdownWidget's REST fetch, the
   *  source of truth for exact counts. */
  setCounts: (counts: ApprovalCounts) => void
  /** Re-fetch exact counts from the approvals endpoints and replace them.
   *  Call after making an approval decision so the header badge updates
   *  immediately instead of waiting for the next dropdown open. */
  refresh: () => Promise<void>
  /** A new approval was requested and the current agent is the approver. */
  bumpPendingForApprover: () => void
  /** A new approval was requested and the current agent is the requester
   *  (the agent whose drafted message needs approval). */
  bumpPendingForRequester: () => void
  /** A pending approval was decided; moves the count for whichever side
   *  (approver/requester) the current agent was on from pending to the
   *  matching approved/rejected bucket. Floors at 0 — a decided event for
   *  an approval this session never saw as pending must not go negative. */
  resolvePending: (role: 'approver' | 'requester', decision: 'approved' | 'rejected') => void
}

export const useApprovalCountsStore = create<ApprovalCountsState>()((set) => ({
  counts: EMPTY,
  setCounts: (counts) => set({ counts }),
  refresh: async () => {
    try {
      const [myRes, reqRes] = await Promise.all([
        api.get<{ conversations: ApprovalRow[] }>('/conversations/my-approvals'),
        api.get<{ conversations: ApprovalRow[] }>('/conversations/requested-approvals'),
      ])
      const myList = myRes.conversations ?? []
      const reqList = reqRes.conversations ?? []
      set({
        counts: {
          pending: myList.filter((r) => !r.reviewed).length,
          approved: myList.filter(isApproved).length,
          rejected: myList.filter(isRejected).length,
          requested: reqList.length,
          requestedApproved: reqList.filter(isApproved).length,
          requestedRejected: reqList.filter(isRejected).length,
          requestedPending: reqList.filter((r) => !r.reviewed).length,
        },
      })
    } catch {
      // Leave the existing counts in place on a transient failure.
    }
  },
  bumpPendingForApprover: () =>
    set((state) => ({
      counts: { ...state.counts, pending: state.counts.pending + 1 },
    })),
  bumpPendingForRequester: () =>
    set((state) => ({
      counts: {
        ...state.counts,
        requested: state.counts.requested + 1,
        requestedPending: state.counts.requestedPending + 1,
      },
    })),
  resolvePending: (role, decision) =>
    set((state) => {
      if (role === 'approver') {
        const pending = Math.max(0, state.counts.pending - 1)
        return {
          counts: {
            ...state.counts,
            pending,
            approved: decision === 'approved' ? state.counts.approved + 1 : state.counts.approved,
            rejected: decision === 'rejected' ? state.counts.rejected + 1 : state.counts.rejected,
          },
        }
      }
      const requestedPending = Math.max(0, state.counts.requestedPending - 1)
      return {
        counts: {
          ...state.counts,
          requestedPending,
          requestedApproved: decision === 'approved' ? state.counts.requestedApproved + 1 : state.counts.requestedApproved,
          requestedRejected: decision === 'rejected' ? state.counts.requestedRejected + 1 : state.counts.requestedRejected,
        },
      }
    }),
}))
